import { useEffect, useState } from "react";
import {
  User,
  Heart,
  Phone,
  Wallet,
  Users2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import { Label } from "@project/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  FormStepper,
  type FormStep,
} from "@project/user/components/form-stepper";
import { formatDate } from "@project/lib/date-format";
import { getApiErrorMessage } from "@project/lib/api-error";
import { elderAdmissionService } from "@project/services/admin/elder-admission.service";
import {
  formatAdmissionNumber,
  normalizeAdmissionStatus,
  type ElderAdmissionRecord,
  type ElderAdmissionStatus,
} from "@project/types/elder-admission";

type StepConfig = FormStep & {
  icon: LucideIcon;
  eyebrow: string;
  subtitle: string;
};

const STEPS: StepConfig[] = [
  {
    label: "Applicant Information",
    shortLabel: "Applicant",
    icon: User,
    eyebrow: "Step 1",
    subtitle: "About the person who submitted this form",
  },
  {
    label: "Elder Information",
    shortLabel: "Elder",
    icon: Heart,
    eyebrow: "Step 2",
    subtitle: "About the elderly person being recommended",
  },
  {
    label: "Income Information",
    shortLabel: "Income",
    icon: Wallet,
    eyebrow: "Step 3",
    subtitle: "Helps assess eligibility and seva category",
  },
  {
    label: "Contact Persons",
    shortLabel: "Contacts",
    icon: Phone,
    eyebrow: "Step 4",
    subtitle: "Local contacts provided with the application",
  },
  {
    label: "Family Details & Remarks",
    shortLabel: "Family",
    icon: Users2,
    eyebrow: "Step 5",
    subtitle: "Family members and additional notes",
  },
];

function statusBadge(status: ElderAdmissionStatus) {
  const normalized = normalizeAdmissionStatus(status);
  const map = {
    pending: {
      label: "Pending",
      className: "bg-amber-500/15 text-amber-700 hover:bg-amber-500/15",
    },
    approved: {
      label: "Approved",
      className: "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15",
    },
    rejected: {
      label: "Rejected",
      className: "bg-rose-500/15 text-rose-700 hover:bg-rose-500/15",
    },
  } as const;
  const item = map[normalized];
  return <Badge className={item.className}>{item.label}</Badge>;
}

function ViewField({
  label,
  value,
  full = false,
}: {
  label: string;
  value?: string | number | null;
  full?: boolean;
}) {
  const text =
    value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
        {label}
      </Label>
      <div className="bg-muted/40 whitespace-pre-wrap rounded-xl border border-transparent px-3 py-2.5 text-sm text-foreground">
        {text}
      </div>
    </div>
  );
}

function StepPanel({
  step,
  children,
}: {
  step: StepConfig;
  children: React.ReactNode;
}) {
  const Icon = step.icon;
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-start gap-3 border-b border-border pb-4">
        <div className="bg-primary/10 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
            {step.eyebrow}
          </div>
          <h3 className="font-serif text-lg font-bold text-foreground sm:text-xl">
            {step.label}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
            {step.subtitle}
          </p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

export type AdmissionDetailViewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  admissionId: number | null;
  preview?: ElderAdmissionRecord | null;
};

export function AdmissionDetailViewDialog({
  open,
  onOpenChange,
  admissionId,
  preview,
}: AdmissionDetailViewDialogProps) {
  const [step, setStep] = useState(0);
  const [record, setRecord] = useState<ElderAdmissionRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  useEffect(() => {
    if (!open) {
      setStep(0);
      setRecord(null);
      setError(null);
      return;
    }
    if (!admissionId) {
      return;
    }

    let ignore = false;
    setLoading(true);
    setError(null);
    setRecord(preview ?? null);

    void elderAdmissionService
      .getAdmission(admissionId)
      .then(data => {
        if (ignore) {
          return;
        }
        if (data) {
          setRecord(data);
        } else {
          setError("Application details could not be loaded.");
        }
      })
      .catch(err => {
        if (!ignore) {
          setError(
            getApiErrorMessage(err, "Failed to load application details.")
          );
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [open, admissionId, preview]);

  const locationLabel = [
    record?.district_name,
    record?.city_name,
    record?.area_name,
  ]
    .filter(Boolean)
    .join(", ");
  const relationshipLabel = record?.relationship_with_elder_name
    ? record.relationship_with_elder_code
      ? `${record.relationship_with_elder_name} (${record.relationship_with_elder_code})`
      : record.relationship_with_elder_name
    : "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
          <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
            <div>
              <DialogTitle className="font-serif text-xl font-bold">
                {record ? formatAdmissionNumber(record) : "Application Details"}
              </DialogTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {record
                  ? `${record.elder_name} · submitted ${formatDate(record.created_at) || "—"}`
                  : "Read-only view of the admission application"}
              </p>
            </div>
            {record && statusBadge(record.status)}
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading && !record ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mb-3 h-8 w-8 animate-spin" />
              Loading application…
            </div>
          ) : error && !record ? (
            <div className="border-destructive/30 bg-destructive/5 rounded-xl border px-4 py-6 text-center text-sm text-destructive">
              {error}
            </div>
          ) : record ? (
            <>
              <FormStepper steps={STEPS} currentStep={step} className="mb-6" />

              {step === 0 && (
                <StepPanel step={current}>
                  <ViewField
                    label="Applicant Name"
                    value={record.applicant_name}
                  />
                  <ViewField
                    label="Mobile Number"
                    value={record.applicant_mobile}
                  />
                  <ViewField
                    label="Address"
                    value={record.applicant_address}
                    full
                  />
                  <ViewField
                    label="Relationship with Elder"
                    value={relationshipLabel}
                  />
                </StepPanel>
              )}

              {step === 1 && (
                <StepPanel step={current}>
                  <ViewField label="Elder Name" value={record.elder_name} />
                  <ViewField label="Father's Name" value={record.father_name} />
                  <ViewField label="Age" value={record.age} />
                  <ViewField label="District" value={record.district_name} />
                  <ViewField label="City" value={record.city_name} />
                  <ViewField label="Area" value={record.area_name} />
                  <ViewField label="Location" value={locationLabel} full />
                  <ViewField
                    label="Current Living Situation"
                    value={record.current_living_situation}
                    full
                  />
                  <ViewField
                    label="Physical Condition"
                    value={record.physical_condition}
                    full
                  />
                  <ViewField
                    label="Reason for Admission"
                    value={record.reason_for_admission}
                    full
                  />
                </StepPanel>
              )}

              {step === 2 && (
                <StepPanel step={current}>
                  <ViewField
                    label="Pension Details"
                    value={record.pension_details}
                  />
                  <ViewField label="Other Income" value={record.other_income} />
                </StepPanel>
              )}

              {step === 3 && (
                <StepPanel step={current}>
                  <ViewField
                    label="Contact Person 1 (Name + Mobile)"
                    value={record.emergency_contact_1}
                  />
                  <ViewField
                    label="Contact Person 2 (Name + Mobile)"
                    value={record.emergency_contact_2}
                  />
                  <ViewField
                    label="Contact Person 3 (Name + Mobile)"
                    value={record.emergency_contact_3}
                    full
                  />
                </StepPanel>
              )}

              {step === 4 && (
                <StepPanel step={current}>
                  <div className="md:col-span-2">
                    {(record.family_members?.length ?? 0) > 0 ? (
                      <div className="overflow-x-auto rounded-xl border border-border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Relationship</TableHead>
                              <TableHead>Mobile</TableHead>
                              <TableHead>Address</TableHead>
                              <TableHead>Occupation</TableHead>
                              <TableHead>Income</TableHead>
                              <TableHead>Emergency</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {record.family_members!.map((member, i) => (
                              <TableRow key={member.id ?? i}>
                                <TableCell className="font-medium">
                                  {member.name || "—"}
                                </TableCell>
                                <TableCell>
                                  {member.relationship_name
                                    ? member.relationship_code
                                      ? `${member.relationship_name} (${member.relationship_code})`
                                      : member.relationship_name
                                    : "—"}
                                </TableCell>
                                <TableCell>
                                  {member.mobile_number || "—"}
                                </TableCell>
                                <TableCell>{member.address ?? "—"}</TableCell>
                                <TableCell>
                                  {member.occupation ?? "—"}
                                </TableCell>
                                <TableCell>{member.income ?? "—"}</TableCell>
                                <TableCell>
                                  {member.is_emergency_contact ? "Yes" : "No"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="bg-muted/30 rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                        No family members added.
                      </div>
                    )}
                  </div>
                  <ViewField
                    label="Additional Remarks"
                    value={record.additional_notes}
                    full
                  />
                </StepPanel>
              )}
            </>
          ) : null}
        </div>

        {record && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(s => s - 1)}
              disabled={isFirst}
              className="gap-1 rounded-xl"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <p className="text-xs text-muted-foreground">
              Step {step + 1} of {STEPS.length}
            </p>
            {isLast ? (
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-xl"
              >
                Close
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => setStep(s => s + 1)}
                className="gap-1 rounded-xl"
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
